import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useSite } from '../content/SiteContext';

const gradientStyle = {
  background: 'linear-gradient(-45deg, #1e1b4b, #4c1d95, #be185d, #1e293b)',
  backgroundSize: '400% 400%',
  animation: 'gradientBG 15s ease infinite',
  width: '100%',
  height: '100%',
  position: 'absolute',
  inset: 0,
  zIndex: 0,
};

// Agregamos la animación global al documento si no existe
if (typeof document !== 'undefined' && !document.getElementById('gradientBGKeyframes')) {
  const style = document.createElement('style');
  style.id = 'gradientBGKeyframes';
  style.innerHTML = `
    @keyframes gradientBG {
      0% { background-position: 0% 50%; }
      50% { background-position: 100% 50%; }
      100% { background-position: 0% 50%; }
    }
  `;
  document.head.appendChild(style);
}

const HeroSection = () => {
  const [isMobile, setIsMobile] = useState(false);
  const { content, t } = useSite();
  const hero = content.hero;

  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  const scrollToServices = () => {
    const servicesSection = document.getElementById('servicios');
    if (servicesSection) {
      servicesSection.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  return (
    <section id="inicio" className="relative h-screen flex items-center justify-center text-center overflow-hidden">
      {/* Fondo animado tipo gradiente CSS */}
      <div style={gradientStyle}></div>
      {/* Capa de oscurecimiento para mejor contraste del texto */}
      <div className="absolute inset-0 bg-black opacity-40 z-10"></div>
      <div className="relative z-20 text-white p-4 md:p-8">
        <motion.h1
          className="text-4xl sm:text-5xl md:text-6xl lg:text-8xl font-extrabold mb-4 md:mb-6 leading-tight"
          style={{
            fontSize: 'clamp(2.5rem, 8vw, 4rem)',
            fontWeight: 800,
            marginBottom: '1rem',
            textAlign: 'center',
            fontFamily: 'Inter, sans-serif',
            lineHeight: '1.1'
          }}
          initial={{ 
            opacity: 0, 
            y: isMobile ? -30 : -50,
            scale: isMobile ? 0.9 : 1
          }}
          animate={{ 
            opacity: 1, 
            y: 0,
            scale: 1
          }}
          transition={{ 
            duration: isMobile ? 0.8 : 1, 
            ease: isMobile ? [0.25, 0.1, 0.25, 1] : "easeOut",
            type: isMobile ? "spring" : "tween",
            stiffness: isMobile ? 150 : undefined,
            damping: isMobile ? 20 : undefined
          }}
        >
          <motion.span 
            className="block"
            initial={{ opacity: 0, x: isMobile ? -20 : 0 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ 
              duration: isMobile ? 0.6 : 0.8, 
              delay: isMobile ? 0.2 : 0.3,
              ease: isMobile ? [0.4, 0, 0.2, 1] : "easeOut"
            }}
          >
            {t(hero.title1)}
          </motion.span>
          <motion.span
            className="block"
            style={{
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              color: 'transparent',
              backgroundImage: 'linear-gradient(to right, #a78bfa, #f472b6)'
            }}
            initial={{ opacity: 0, x: isMobile ? 20 : 0 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ 
              duration: isMobile ? 0.6 : 0.8, 
              delay: isMobile ? 0.4 : 0.5,
              ease: isMobile ? [0.4, 0, 0.2, 1] : "easeOut"
            }}
          >
            {t(hero.title2)}
          </motion.span>
        </motion.h1>
        <motion.p
          className="text-base sm:text-lg md:text-xl lg:text-2xl font-light mb-6 md:mb-8 max-w-3xl mx-auto px-4"
          style={{
            fontSize: 'clamp(1rem, 4vw, 1.25rem)',
            fontWeight: 300,
            lineHeight: '1.6',
            color: 'rgba(255, 255, 255, 0.9)'
          }}
          initial={{ 
            opacity: 0, 
            y: isMobile ? 30 : 50,
            scale: isMobile ? 0.95 : 1
          }}
          animate={{ 
            opacity: 1, 
            y: 0,
            scale: 1
          }}
          transition={{ 
            duration: isMobile ? 0.7 : 1, 
            ease: isMobile ? [0.25, 0.1, 0.25, 1] : "easeOut", 
            delay: isMobile ? 0.6 : 0.5,
            type: isMobile ? "spring" : "tween",
            stiffness: isMobile ? 120 : undefined,
            damping: isMobile ? 18 : undefined
          }}
        >
          {t(hero.subtitle)}
        </motion.p>
                 <motion.button
           onClick={scrollToServices}
           className="relative px-6 md:px-8 py-3 md:py-4 bg-gradient-to-r from-pink-500 to-purple-500 text-white font-bold rounded-full shadow-lg overflow-hidden text-sm md:text-base"
           initial={{ 
             opacity: 0, 
             scale: isMobile ? 0.85 : 0.8, 
             y: isMobile ? 15 : 20 
           }}
           animate={{ opacity: 1, scale: 1, y: 0 }}
           transition={{ 
             duration: isMobile ? 1 : 1.2, 
             ease: isMobile ? [0.25, 0.1, 0.25, 1] : [0.4, 0, 0.2, 1], 
             delay: isMobile ? 0.8 : 1,
             type: "spring",
             stiffness: isMobile ? 140 : 100,
             damping: isMobile ? 18 : 15
           }}
           whileHover={{ 
             y: isMobile ? -5 : -8, 
             scale: isMobile ? 1.03 : 1.05,
             boxShadow: isMobile ? "0 15px 30px rgba(236, 72, 153, 0.5)" : "0 20px 40px rgba(236, 72, 153, 0.4)",
             transition: { 
               duration: isMobile ? 0.2 : 0.3,
               type: "spring",
               stiffness: isMobile ? 400 : 300,
               damping: isMobile ? 25 : 20
             }
           }}
           whileTap={{ 
             scale: isMobile ? 0.97 : 0.95,
             y: isMobile ? -1 : -2,
             transition: { duration: isMobile ? 0.05 : 0.1 }
           }}
         >
           {/* Efecto de brillo animado */}
           <motion.div
             className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
             initial={{ x: "-100%" }}
             animate={{ x: "100%" }}
             transition={{
               duration: isMobile ? 1.5 : 2,
               repeat: Infinity,
               repeatDelay: isMobile ? 2 : 3,
               ease: "linear"
             }}
           />
           
           {/* Gradiente de fondo animado */}
           <motion.div
             className="absolute inset-0 bg-gradient-to-r from-pink-600 via-purple-600 to-pink-600"
             animate={{
               backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"]
             }}
             transition={{
               duration: isMobile ? 3 : 4,
               repeat: Infinity,
               ease: "linear"
             }}
             style={{
               backgroundSize: "200% 200%"
             }}
           />
           
           <span className="relative z-10">{t(hero.cta)}</span>
         </motion.button>
      </div>
    </section>
  );
};

export default HeroSection; 